import { useAsyncExecutor } from "async-executor-hook";
import { Paginator } from "primereact/paginator";
import { useEffect, useState } from "react";

import { Authorization } from "../../common/AuthorizationCpt";
import { getErrorValueResponse } from "../../common/error/Error";
import { generateSequentialArray } from "../../common/utils";
import { HTTP_METHODS } from "../../utils/constants";
import { dateToRegularFormat } from "../../utils/dates";
import { networkBackendRequest } from "../../utils/networkUtils";
import CommentModal from "../CommentModal/CommentModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  Container,
  FakeRow,
  Row,
  TableBody,
  TableHeader,
} from "../DocumentManagement/DocumentManagementSharedStyles";
import FileUploadMandateModal from "../FileUploadMandateModal/FileUploadMandateModal";
import StandaloneMessage from "../StandaloneMessage/StandaloneMessage";
import StatusTag from "../StatusTag/StatusTag";
import { Span, Title } from "./AffectedDamsStyles";
import { convertElectronic } from "./ConvertSenasaElectronic";
import ElectronicDamsFilter, {
  formatFilters,
  INITIAL_FILTERS,
} from "./ElectronicDamsFilter";

const ITEMS_PER_PAGE = 10;

const TITLES = {
  dam: "DAM/CONSIGNATARIO",
  status: "ESTADO",
  date: "FECHA DE ACTUALIZACIÓN DEL DOCUMENTO",
  volantes: "VOLANTES FACTURADOS",
};

const ElectronicDams = () => {
  const auxiliaryArray = generateSequentialArray(ITEMS_PER_PAGE);

  const getElectronicMandate = useAsyncExecutor(
    async (params: any) => {
      const paramsFilter = formatFilters(params.filters);
      const paramsSearch = {
        ...paramsFilter,
        order: "descending",
        page_size: ITEMS_PER_PAGE,
        page: params.page ?? 1,
      };
      return await networkBackendRequest(
        "/dams/electronic-mandate-agent/",
        HTTP_METHODS.GET,
        paramsSearch
      )
        .then((resp) => resp.json())
        .then(convertElectronic);
    },

    {
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setMessageErrorVolantes(errorMessage);
        setShowModalErrorVolantes(true);
      },
    }
  );

  useEffect(() => {
    getElectronicMandate.execute({ filters });
    // eslint-disable-next-line
  }, []);

  const [filters, updateFilters] = useState(INITIAL_FILTERS);

  const [messageErrorVolantes, setMessageErrorVolantes] = useState("");
  const [showModalErrorVolantes, setShowModalErrorVolantes] = useState(false);
  const [showMandateModal, updateShowMandateModal] = useState(false);
  const [selectedDam, updateSelectedDam] = useState(0);
  const [showCommentModal, updateShowCommentModal] = useState(false);
  const [damComment, updateDamComment] = useState(false);

  const toggleCommentModal = () => {
    updateShowCommentModal(!showCommentModal);
  };

  return (
    <div>
      {messageErrorVolantes && showModalErrorVolantes && (
        <ConfirmationModal
          message={messageErrorVolantes}
          onAccept={() => {
            setShowModalErrorVolantes(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowModalErrorVolantes(false);
          }}
        />
      )}

      <Title>DAM Afectadas</Title>
      <ElectronicDamsFilter
        onQuery={(filters) => {
          getElectronicMandate.execute({ filters });
          updateFilters(filters);
        }}
      />

      {getElectronicMandate.isLoading &&
        auxiliaryArray.map((_, index) => {
          return <FakeRow key={index} />;
        })}
      {getElectronicMandate.isSuccess &&
        getElectronicMandate.data.fetchedDams.length === 0 && (
          <StandaloneMessage>No se encontraron datos.</StandaloneMessage>
        )}
      {getElectronicMandate.isError && (
        <div>
          Algo salió mal
          <button
            type="button"
            onClick={() => {
              getElectronicMandate.retry();
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      {getElectronicMandate.isSuccess &&
        getElectronicMandate.data.fetchedDams.length > 0 && (
          <Container gridTemplateColumns="repeat(4, 1fr)">
            <TableHeader className="item-container">
              <div className="left-align">{TITLES.dam}</div>
              <div>{TITLES.status}</div>
              <div className="center">{TITLES.date}</div>
              <div>{TITLES.volantes}</div>
            </TableHeader>
            <TableBody>
              {getElectronicMandate.data.fetchedDams.map((dam) => {
                const id = dam.id;
                const prefixDamId = id.slice(0, id.lastIndexOf("-") + 1);
                const suffixDamId = id.slice(id.lastIndexOf("-") + 1);

                const defineClickable = (damNum) => {
                  if (dam.status.label === "Rechazado") {
                    updateSelectedDam(damNum);
                    updateDamComment(dam.electronicMandateComment);
                    updateShowCommentModal(true);
                  }
                };
                const className = `item-container ${
                  dam.status.label === "Rechazado" ? "clickable" : ""
                }`;
                return (
                  <Row
                    key={dam.id}
                    className={className}
                    onClick={() => defineClickable(dam.id)}
                  >
                    <div className="attribute left-align">
                      <div className="titles">{TITLES.dam}</div>
                      <div className="left-align">
                        <div>
                          <Span>{prefixDamId}</Span>
                          {suffixDamId}
                        </div>
                        <div>{dam.consigneeName}</div>
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.status}</div>
                      <Authorization
                        actions={[
                          "dam.get_dam_senasa_agent",
                          "dam.get_dam_electronic_mandate_agent",
                          "dam.update_dam_senasa_agent",
                          "dam.update_dam_electronic_mandate_agent",
                        ]}
                      >
                        <div>
                          <StatusTag status={dam.status} />
                        </div>
                      </Authorization>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.date}</div>
                      <div>
                        {" "}
                        {dam.updated_at
                          ? dateToRegularFormat(dam.updated_at)
                          : "-"}
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.volantes}</div>
                      <div>{`${dam.paidVolantesNum}/${dam.volantesNum} volantes`}</div>
                    </div>
                  </Row>
                );
              })}
            </TableBody>

            <div className="flex-paginator">
              <Authorization
                actions={[
                  "dam.get_dam_senasa_agent",
                  "dam.get_dam_electronic_mandate_agent",
                ]}
              >
                <Paginator
                  first={
                    (getElectronicMandate.data.page - 1) *
                    getElectronicMandate.data.page_size
                  }
                  rows={getElectronicMandate.data.page_size}
                  totalRecords={getElectronicMandate.data.count}
                  onPageChange={(e) => {
                    const targetPage =
                      e.first / getElectronicMandate.data.page_size;
                    getElectronicMandate.execute({
                      page: targetPage + 1,
                      filters,
                    });
                  }}
                />
              </Authorization>
            </div>
          </Container>
        )}
      {showCommentModal && (
        <CommentModal
          toggleModal={toggleCommentModal}
          title="Documento rechazado"
          message={damComment}
          onAccept={() => {
            updateShowMandateModal(true);
            toggleCommentModal();
          }}
        />
      )}
      {showMandateModal && (
        <FileUploadMandateModal
          damNum={selectedDam}
          hideModal={() => updateShowMandateModal(false)}
          getSenasaAndElectronicMandate={getElectronicMandate}
        />
      )}
    </div>
  );
};

export default ElectronicDams;
